import type { Message, MessageStatus, SendMessage, SendTemplate } from '../types/message';
import baseApi from './baseApi';

const messagesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getClientMessages: build.query({
      query: (clientId: string) => ({
        url: `/clients/${clientId}/messages`,
        method: 'GET',
      }),
      transformResponse: (response: Message[]) => response,
    }),
    getMessage: build.query({
      query: (messageId: number) => ({
        url: `/admin/messages/${messageId}`,
        method: 'GET',
      }),
      transformResponse: (response: Message) => response,
    }),
    postMessage: build.mutation({
      query: (body: SendMessage) => ({
        url: `/clients/${body.clientId}/messages`,
        method: 'POST',
        body: {
          text: body.text,
        },
      }),
      transformResponse: (response: Message) => response,
    }),
    postTemplateMessage: build.mutation({
      query: (body: SendTemplate) => ({
        url: `/clients/${body.clientId}/template-messages`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: Message) => response,
    }),
    postFileMessage: build.mutation({
      // Either an uploaded file, or a quick reply file already in the bucket sent by path.
      query: (props: { clientId: number } & ({ file: File } | { path: string; fileName: string })) => {
        const url = `/clients/${props.clientId}/send-file`;
        if ('file' in props) {
          const formData = new FormData();
          formData.append('file', props.file);
          return { url, method: 'POST', body: formData };
        }
        return { url, method: 'POST', body: { path: props.path, fileName: props.fileName } };
      },
      transformResponse: (response: Message) => response,
    }),
    // Email-only: sends the text and every attached file as a single email (one request → one email).
    // Hits the same text endpoint as postMessage, but multipart so it can carry files.
    postEmailMessage: build.mutation({
      query: (props: { clientId: number; text: string; files: File[] }) => {
        const formData = new FormData();
        formData.append('text', props.text);
        props.files.forEach((file) => formData.append('file', file));
        return { url: `/clients/${props.clientId}/messages`, method: 'POST', body: formData };
      },
      transformResponse: (response: { messages: Message[] }) => response.messages,
    }),
    postAudioMessage: build.mutation({
      query: (props: { audio: Blob; clientId: number }) => {
        const formData = new FormData();
        formData.append('audio', props.audio, 'voice.ogg');
        return {
          url: `/clients/${props.clientId}/send-audio`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: Message) => response,
    }),
    getNewClientMessages: build.mutation({
      query: (props: { clientId: string; messageId: number }) => ({
        url: `/clients/${props.clientId}/messages/${props.messageId}`,
        method: 'GET',
      }),
      transformResponse: (response: {
        newMessages: Message[];
        oldMessagesStatuses: { id: number; status: MessageStatus }[];
      }) => response,
    }),
    typing: build.mutation({
      query: (props: { clientId: number }) => ({
        url: `/clients/${props.clientId}/typing`,
        method: 'POST',
        body: {},
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetClientMessagesQuery,
  useGetMessageQuery,
  usePostMessageMutation,
  usePostTemplateMessageMutation,
  usePostFileMessageMutation,
  usePostEmailMessageMutation,
  usePostAudioMessageMutation,
  useGetNewClientMessagesMutation,
  useTypingMutation,
} = messagesApi;
